import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MdKeyboardArrowLeft, MdOpenInNew } from 'react-icons/md';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import useFetchJobSource from '../api/useFetchJobSource';
import useDeleteJobSource from '../api/useDeleteJobSource';
import usePostJobSourceReload from '../api/usePostJobSourceReload';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import LinkedAccountDetailHeader from '../components/detail/LinkedAccountDetailHeader';
import LinkedAccountFieldRow from '../components/detail/LinkedAccountFieldRow';
import LinkedAccountDesync from '../components/modals/LinkedAccountDesync';
import LinkedAccountEditTitleModal from '../components/modals/LinkedAccountEditTitleModal';
import LinkedAccountEditLoginModal from '../components/modals/LinkedAccountEditLoginModal';
import LinkedAccountEditAccessUrlModal from '../components/modals/LinkedAccountEditAccessUrlModal';
import LinkedAccountEditBasicAuthModal from '../components/modals/LinkedAccountEditBasicAuthModal';

type EditTarget = 'title' | 'login' | 'accessUrl' | 'basicAuth' | null;

type Props = {
  id: number;
  from?: 'shared';
};

export default function LinkedAccountDetailTemplate({ id, from }: Props) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useFetchJobSource(id);
  const deleteJobSource = useDeleteJobSource();
  const postJobSourceReload = usePostJobSourceReload();
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [isDesyncModalOpen, setIsDesyncModalOpen] = useState(false);

  const listPath = from === 'shared' ? '/agent/linked_accounts/shared' : '/agent/linked_accounts';
  const goToList = () => navigate({ to: listPath });

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh_-_200px)] items-center justify-center">
        <Loading size="medium" />
      </div>
    );
  }

  if (isError || !data?.id) {
    return (
      <div className="flex h-[calc(100vh_-_200px)] flex-col items-center justify-center gap-md">
        <p className="text-body-md">連携先が見つかりませんでした。</p>
        <Button type="button" color="secondary" onClick={goToList}>
          <MdKeyboardArrowLeft size={20} />
          連携先一覧へ戻る
        </Button>
      </div>
    );
  }

  const isBasicAuthType = data.auth_type === 'ID_PASSWORD_URL_BASIC_AUTH';
  // HERP(AGENT_URL)はログイン要否が企業によって異なるため、login_id 未登録でも編集UIを表示する
  const showLoginSection = Boolean(data.login_id) || data.auth_type === 'AGENT_URL';

  const handleReload = async () => {
    try {
      await postJobSourceReload.mutateAsync({ id });
      await refetch();
      showSuccessToast('同期が開始されました');
    } catch {
      showErrorToast('再同期に失敗しました');
    }
  };

  const handleConfirmDesync = async () => {
    try {
      await deleteJobSource.mutateAsync({ id });
      setIsDesyncModalOpen(false);
      showSuccessToast('同期を解除しました');
      goToList();
    } catch {
      showErrorToast('同期解除に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-md px-lg pt-sm pb-lg">
      <LinkedAccountDetailHeader
        jobSource={data}
        onEditTitle={() => setEditTarget('title')}
        onDesync={() => setIsDesyncModalOpen(true)}
        onReload={handleReload}
        isReloading={postJobSourceReload.isPending}
      />

      {data.access_url && (
        <LinkedAccountFieldRow
          heading="アカウント情報"
          canEdit={data.is_able_to_edit}
          onEdit={() => setEditTarget('accessUrl')}
        >
          <div className="flex items-start gap-xxs">
            <a
              href={data.access_url}
              target="_blank"
              rel="noopener noreferrer"
              className="line-clamp-2 min-w-0 text-body-sm break-all text-primary underline hover:opacity-80"
            >
              {data.access_url}
            </a>
            <MdOpenInNew size={16} className="mt-[3px] shrink-0 text-primary" />
          </div>
        </LinkedAccountFieldRow>
      )}

      {isBasicAuthType && (
        <LinkedAccountFieldRow
          heading="Basic 認証"
          canEdit={data.is_able_to_edit}
          onEdit={() => setEditTarget('basicAuth')}
        >
          <div className="flex flex-col gap-md">
            <div className="flex w-[450px] flex-col gap-xs">
              <label className="text-heading-xxs">ID</label>
              <p className="text-body-sm">
                {data.basic_auth_id || <span className="text-sub">未登録</span>}
              </p>
            </div>
            <div className="flex w-[450px] flex-col gap-xs">
              <label className="text-heading-xxs">パスワード</label>
              <p className="text-body-sm text-sub">{data.basic_auth_id ? '••••••••' : '未登録'}</p>
            </div>
          </div>
        </LinkedAccountFieldRow>
      )}

      {showLoginSection && (
        <LinkedAccountFieldRow
          heading="ログイン情報"
          canEdit={data.is_able_to_edit}
          onEdit={() => setEditTarget('login')}
        >
          <div className="flex flex-col gap-md">
            <div className="flex w-[450px] flex-col gap-xs">
              <label className="text-heading-xxs">ログインID</label>
              <p className="text-body-sm">
                {data.login_id || <span className="text-sub">未登録</span>}
              </p>
            </div>
            <div className="flex w-[450px] flex-col gap-xs">
              <label className="text-heading-xxs">パスワード</label>
              <p className="text-body-sm text-sub">{data.login_id ? '••••••••' : '未登録'}</p>
            </div>
          </div>
        </LinkedAccountFieldRow>
      )}

      {editTarget === 'title' && (
        <LinkedAccountEditTitleModal
          isOpen
          jobSourceId={id}
          currentTitle={data.title}
          sourceLabel={data.source_display_type || 'タイトル'}
          onClose={() => setEditTarget(null)}
          onSaved={refetch}
        />
      )}
      {editTarget === 'login' && (
        <LinkedAccountEditLoginModal
          isOpen
          jobSourceId={id}
          currentLoginId={data.login_id}
          onClose={() => setEditTarget(null)}
          onSaved={refetch}
        />
      )}
      {editTarget === 'accessUrl' && (
        <LinkedAccountEditAccessUrlModal
          isOpen
          jobSourceId={id}
          currentAccessUrl={data.access_url}
          onClose={() => setEditTarget(null)}
          onSaved={refetch}
        />
      )}
      {editTarget === 'basicAuth' && (
        <LinkedAccountEditBasicAuthModal
          isOpen
          jobSourceId={id}
          currentBasicAuthId={data.basic_auth_id}
          onClose={() => setEditTarget(null)}
          onSaved={refetch}
        />
      )}

      <LinkedAccountDesync
        isOpen={isDesyncModalOpen}
        onClose={() => setIsDesyncModalOpen(false)}
        onConfirm={handleConfirmDesync}
        isPending={deleteJobSource.isPending}
      />
    </div>
  );
}
